import { Box, Chip, Alert, IconButton, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FileRecord } from "../types";
import CustomizedDataGrid from "../../customized-data-grid/customized-data-grid";

interface FileDetailsProps {
  files: FileRecord[];
  onDelete?: (file: FileRecord) => void;
  onDownload?: (file: FileRecord) => void;
  onReprocess?: (file: FileRecord) => void;
}

const FileDetails = ({
  files,
  onDelete,
  onDownload,
  onReprocess,
}: FileDetailsProps) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.5,
      minWidth: 80,
    },
    {
      field: "fileName",
      headerName: "File Name",
      flex: 2,
      minWidth: 250,
    },
    {
      field: "fileSize",
      headerName: "Size",
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) => formatFileSize(params),
    },
    {
      field: "fileType",
      headerName: "Type",
      flex: 0.8,
      minWidth: 80,
      valueGetter: (params, row) => row.fileType?.extension || "N/A",
    },
    {
      field: "dateImported",
      headerName: "Import Date",
      flex: 1.5,
      minWidth: 180,
      valueFormatter: (params) => formatDate(params),
    },
    {
      field: "dateProcessed",
      headerName: "Processed Date",
      flex: 1.5,
      minWidth: 180,
      valueFormatter: (params) => formatDate(params),
    },
    {
      field: "processed",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Processed" : "Pending"}
          color={params.value ? "success" : "warning"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.2,
      minWidth: 150,
      sortable: false,
      filterable: false,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
          {onDownload && (
            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(params.row);
                }}
                sx={{ color: "primary.main" }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onReprocess && (
            <Tooltip title="Reprocess file from beginning (use only when batch was not created)">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onReprocess(params.row);
                }}
                sx={{ color: "info.main" }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(params.row);
                }}
                sx={{ color: "error.main" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (!files || files.length === 0) {
    return <Alert severity="info">No files found for this client.</Alert>;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <CustomizedDataGrid
        data={files}
        columns={columns}
        isLoading={false}
        initialSortingField="dateImported"
        rowSelection={false}
        height={635}
      />
    </Box>
  );
};

export default FileDetails;
